import {api, RequestParam} from "@/shared/api";
import * as Button from "@/shared/utils/button";
import {RecordingReport, RecordingReportConfig} from "./recordingReport";
import {SetNoteDialog} from "@/features/reports/setNoteDialog";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {ProjectTimeDetailsDialog} from "@/features/projecttime/projectTimeDetailsDialog";
import {ProjectTimeTable} from "@/features/projecttime/projectTimeTable";
import {TaskDropdown} from "@/shared/task/taskDropdown";
import {TaskInfoDialog} from "@/features/task/taskInfoDialog";
import {showProjectTimeBulkResult} from "@/shared/bulkOperationHelper";
import {SetTaskDialog} from "@/features/projecttime/setTaskDialog";
import {SelectProjectTimeReportDialog} from "@/features/projecttime/selectProjectTimeReportDialog";
import {LockTaskDialog} from "@/features/task/lockTaskDialog";
import {RecordingTable} from "@/shared/recording/recordingTable";
import * as RecordingValidationUtil from "@/shared/validations/recordingValidationUtil";
import * as Constants from "@/shared/constants";
import {isProjectTimeDateTimeChangeAllowed} from "@/shared/securityHelper";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {EMPTY_CALLBACK} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDuration, parseISODate} from "@/shared/utils/datefunctions";
import {ProjectTimeBulkOperationResult, ReportProjectTime, Task,TaskDetailsResponse, User} from "@/shared/timr";
import {ExportType, ProjectTimeReportAttachmentsPerRow} from "@/shared/timrTypes";
import {ProjectTimeReportingQueryParams} from "@/shared/queryParams";

declare const c3: typeof import("c3");

export interface ProjectTimeReportConfig extends RecordingReportConfig {
	isTaskLeader?: boolean;
	projectTimeAttachmentsEnabled?: boolean;
}

interface ProjectTimeSumEntry {
	duration: number;
	breakTime: number;
}

interface ProjectTimeStatisticsData {
	projectTimeSums: {
		billableRecordSums?: ProjectTimeSumEntry;
		notBillableRecordSums?: ProjectTimeSumEntry;
	};
}

export class ProjectTimeReportPage extends RecordingReport<ReportProjectTime> {

	declare private setTaskDialog: SetTaskDialog;
	declare private setNoteDialog: SetNoteDialog;
	private projectTimeDialog: ProjectTimeDialog;
	private projectTimeDetailsDialog: ProjectTimeDetailsDialog;
	declare private taskDropdown: TaskDropdown;
	declare private billableChart: c3.ChartAPI;
	private selectProjectTimeReportDialog: SelectProjectTimeReportDialog;
	declare private lockTaskDialog: LockTaskDialog;
	private taskInfoDialog: TaskInfoDialog;

	constructor(public config: ProjectTimeReportConfig) {
		super(config, {
			formSelector: "#projectTimeFilterForm",
			roundingFiltersSelector: "#projectTimeRoundingFilters",
			filterButtonSelector: "#projectTimeFilterButton",
			bulkOperationDropDownSelector: "#projectTimeBulkOperationDropDown",
			bulkOperationButtonSelector: "#projectTimeBulkOperationButton",
			exportDropDownSelector: "#projectTimeExportDropDown",
			exportButtonSelector: "#projectTimeExportButton",
			roundingModeSelector: "#projectTimeRoundingMode",
			roundToMinutesSelector: "#projectTimeRoundToMinutes",
			groupDropdownSelector: "#projectTimeFilterGroupDropdown",
			userIdSelector: "#projectTimeFilterUserId",
			userFilterSelector: "#projectTimeFilterUserFilter",
			userCaretSelector: "#projectTimeFilterUserCaret",
			showInactiveSelector: "#projectTimeReportShowInactive",
			bulkOperationDeleteConfirmMessage: "projecttime.bulkdelete.message",
			bulkOperationDeleteUrl: "/reports/bulkDeleteProjectTimes.json",
			bulkOperationSetStatusUrl: "/reports/updateProjectTimeStatus.json",
			exportBaseUrl: "export/projectTimeReport.json",
			exportStatusBaseUrl: "export/status.json",
			accountExpiredTitle: "account.expired.projecttime.title",
			accountExpired: "account.expired.projecttime",
			dropdownUrls: {
				loadRootNodesURL: "reports/loadRootGroups.json",
				loadSubNodesURL: "reports/loadSubGroups.json",
				filterNodesURL: "reports/filterGroups.json"
			}
		});
	}

	protected override getReportingQueryParams() {
		return ProjectTimeReportingQueryParams;
	}

	protected override applyRecordTypeQueryParams(
		queryParams: typeof ProjectTimeReportingQueryParams,
		pendingHydrationRequests: PromiseLike<unknown>[]
	) {
		const appliedKeys = super.applyRecordTypeQueryParams(queryParams, pendingHydrationRequests);

		const taskId = queryParams.get("taskId");
		if (taskId && this.taskDropdown) {
			const request = api("reports/getTask.json")
				.get({taskId})
				.done(task => {
					if (task?.id) {
						this.taskDropdown.setSelection(task.id, task.name, task.breadCrumbs, task);
						this.groupDropdown?.setTaskId(taskId);
					}
				});
			pendingHydrationRequests.push(request);
			appliedKeys.push("taskId");
		}
		return appliedKeys;
	}

	protected init() {
		super.init();

		this.taskDropdown = new TaskDropdown({
			rootElement: $("#projectTimeFilterTaskDropdown"),
			allowSelectionOfNotBookable: true,
			hideInactive: true,
			clearable: true
		}, {
			loadRootNodesURL: "reports/loadAllTasks.json",
			loadSubNodesURL: "reports/treeLoad.json",
			filterNodesURL: "reports/filterTasks.json"
		});
		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task) => {
			this.filterTable();
			if (SecurityStatus.isCorporateFeaturesAllowed() && (globals.isGroupLeader || globals.isTaskLeader)) {
				const taskId = task ? task.id : null;
				this.groupDropdown.setTaskId(taskId);
				this.reloadUserList();
			}
		});

		if (globals.validateProjectTimeOverlaps || globals.validateProjectTimeExistsWorkingTimes || globals.validateWorkingTime) {
			this.initValidationsListeners();
		}

		$(this.recordingTable).on(ProjectTimeTable.EVENT_DATA_CHANGED, (e, data: ProjectTimeStatisticsData) => {
			this.updateStatistics(data);
		});

		this.recordingTable.setTaskLeader(this.config.isTaskLeader);

		$("#projectTimeReportShowInactive").change(() => {
			this.reloadUserList();
		});

		$("#addProjectTimeButton").click((e) => {
			e.preventDefault();
			this.showAddRecordDialog();
		}).toggleClass("disabled", !isProjectTimeDateTimeChangeAllowed(true));

		const lockTaskButton = $("#projectTimeLockTaskButton");
		if (lockTaskButton.length > 0) {
			this.lockTaskDialog = new LockTaskDialog($("#lockTaskDialog"));
			$(this.lockTaskDialog).on(LockTaskDialog.EVENT_TASK_LOCKED, () => {
				this.reloadTable();
			});
			lockTaskButton.click((e) => {
				e.preventDefault();
				this.lockTaskDialog.show(this.taskDropdown.getSelectedData());
			});
		}

		this.initBillableChart();
		this.setTaskDialog = new SetTaskDialog({
			container: $("#projectTimeSetTaskDialog")
		});
		$(this.setTaskDialog).on(SetTaskDialog.EVENT_TASK_SET, (e, result: ProjectTimeBulkOperationResult) => {
			if (!result.allUpdated) {
				showProjectTimeBulkResult(result, "record.bulksettask.");
			}
			this.reloadTable();
		});

		this.setNoteDialog = new SetNoteDialog({
			container: $("#bulkChangeNoteDialog"),
			apiUrl: "reports/bulkProjectTimesSetNote.json"
		});
		$(this.setNoteDialog).on(SetNoteDialog.EVENT_NOTE_SET, (e, result: ProjectTimeBulkOperationResult) => {
			if (!result.allUpdated) {
				showProjectTimeBulkResult(result, "record.bulksetnote.");
			}
			this.reloadTable();
		});

		$(this.recordingTable).on(ProjectTimeTable.EVENT_SHOW_TASK_DETAILS, (e, recording: ReportProjectTime) => {
			this.showInfoDialog(recording.taskId, recording.userId);
		});
	}

	// override method in base class
	protected reloadUserList() {
		api("reports/filterProjectTimeUsers.json")
			.get(this.buildUserListParams())
			.done((data) => {
				if (data) {
					if (data.users) {
						this.setUsers(data.users);
					}
					this.recordingTable.setTaskLeader(data.isTaskLeader === true);
				}
			});
	}

	protected getRecordById(recordId: number, link: JQuery) {
		return this.getProjectTimeById(recordId, link);
	}

	protected getProjectTimeById(projectTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		api("reports/getProjectTime.json")
			.get({projectTimeId: projectTimeRecordId})
			.done(data => {
				if (this.recordingTable.recordingChangeable(data)) {
					this.edit(data);
				} else {
					this.show(data);
				}
			})
			.always(() => RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link));
	}

	protected createRecordingTable(): RecordingTable<ReportProjectTime> {
		return new ProjectTimeTable({
			dataUrl: ProjectTimeTable.REPORT_PROJECT_TIMES_URL,
			element: $("#projectTimeTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: false,
			allowResume: false,
			allowSelect: true,
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: true,
			orderable: true,
			clampNote: false,
			allowExpand: false,
			pageLength: Constants.REPORT_TABLES_PAGE_LENGTH,
			paging: true,
			excludeElementsDblclickEvent: [RecordingReport.EXCLUDE_ELEMENTS_DBLCLICK_EVENT],
			projectTimeAttachmentsEnabled: this.config.projectTimeAttachmentsEnabled,
		});
	}

	protected onShowInactiveChanged(showInactive: boolean) {
		super.onShowInactiveChanged(showInactive);
		this.taskDropdown.setHideInactive(!showInactive);
	}

	protected buildUserListParams(): RequestParam[] {
		return [
			...super.buildUserListParams(),
			{name: "taskId", value: this.taskDropdown.getSelectedId()},
		];
	}

	protected setUsers(users: User[]) {
		super.setUsers(users);
		if (this.projectTimeDialog != null) {
			this.projectTimeDialog.config.users = users;
		}
	}

	protected bulkOperationSetBillable() {
		const count = this.recordingTable.bulkSelector.getSelectedEntriesCount();
		const confirmMessage = messages.withArguments("report.setBillable.confirm", count);
		BootstrapModal.confirm({
			message: confirmMessage,
			yesLabel: "report.setBillable.changeBillable",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
				Button.setLoading(button);
				api("reports/bulkProjectTimesSetBillable.json")
					.post(this.buildBulkOperationParams())
					.done((result: ProjectTimeBulkOperationResult) => {
						if (!result.allUpdated) {
							showProjectTimeBulkResult(result, "record.bulkbillable.");
						}
						this.reloadTable();
					})
					.always(() => {
						Button.resetLoading(button);
						modal.close();
					});
			}
		});
	}

	protected bulkOperationSetNotBillable() {
		const count = this.recordingTable.bulkSelector.getSelectedEntriesCount();
		const confirmMessage = messages.withArguments("report.setNotBillable.confirm", count);
		BootstrapModal.confirm({
			message: confirmMessage,
			yesLabel: "report.setNotBillable.changeBillable",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal, button?: JQuery) => {
				Button.setLoading(button);
				api("reports/bulkProjectTimesSetNotBillable.json")
					.post(this.buildBulkOperationParams())
					.done((result: ProjectTimeBulkOperationResult) => {
						if (!result.allUpdated) {
							showProjectTimeBulkResult(result, "record.bulkbillable.");
						}
						this.reloadTable();
					})
					.always(() => {
						Button.resetLoading(button);
						modal.close();
					});
			}
		});
	}

	protected show(recording: ReportProjectTime) {
		this.getProjectTimeDetailsDialog().show(recording);
	}

	protected edit(recording: ReportProjectTime) {
		this.getProjectTimeDialog().edit(recording);
	}

	protected duplicate(recording: ReportProjectTime) {
		this.getProjectTimeDialog().duplicate(recording);
	}

	protected requestExport(exportType: ExportType): Promise<string> {
		if (exportType === ExportType.Csv || exportType === ExportType.Excel || exportType === ExportType.Ical) {
			return super.requestExport(exportType, false);
		}
		this.selectProjectTimeReportDialog ??= new SelectProjectTimeReportDialog($("#selectProjectTimeReportDialog"));
		return this.selectProjectTimeReportDialog.show(exportType).then(({grouping, includeAttachments}) => super.requestExport(exportType, false, [
			{name: "grouping", value: grouping},
			{
				name: "attachmentColumns",
				value: includeAttachments ? ProjectTimeReportAttachmentsPerRow.ONE : ProjectTimeReportAttachmentsPerRow.NONE
			}
		]));
	}

	private updateStatistics(data: ProjectTimeStatisticsData) {
		const projectTimeSums = data.projectTimeSums;
		let billable = 0;
		let notBillable = 0;
		if (projectTimeSums.billableRecordSums) {
			billable = projectTimeSums.billableRecordSums.duration - projectTimeSums.billableRecordSums.breakTime * 60 * 1000;
		}
		if (projectTimeSums.notBillableRecordSums) {
			notBillable = projectTimeSums.notBillableRecordSums.duration - projectTimeSums.notBillableRecordSums.breakTime * 60 * 1000;
		}
		const sum = billable + notBillable;
		$("#projectTimeBillableDurationSum").html(formatDuration(billable));
		$("#projectTimeNotBillableDurationSum").html(formatDuration(notBillable));
		$("#projectTimeDurationSum").html(formatDuration(sum));

		$("#projectTimeBillableChart").toggle(sum > 0);
		this.billableChart.load({
			columns: this.buildBillableChartColumns(billable, notBillable, sum),
			names: this.buildBillableChartNames(billable, notBillable, sum)
		});
	}

	private initBillableChart() {
		this.billableChart = c3.generate({
			bindto: "#projectTimeBillableChart",
			size: {width: 270, height: 70},
			data: {
				columns: this.buildBillableChartColumns(0, 0, 0),
				type: "donut",
				names: this.buildBillableChartNames(0, 0, 0),
			},
			legend: {
				position: "right",
				item: {
					onclick: EMPTY_CALLBACK
				}
			},
			donut: {
				label: {
					show: false
				},
				width: 7
			},
			interaction: {
				enabled: false
			}
		});
		$("#projectTimeBillableChart")
			.find(".c3-legend-item").attr("transform", "translate(6, 5)")
			.find(".c3-legend-item-tile").attr("transform", "translate(6, 1)");
	}

	private buildBillableChartColumns(billable: number, notBillable: number, sum: number): c3.PrimitiveArray[] {
		const billablePercent = sum > 0 ? billable / sum : 0.5;
		const notBillablePercent = sum > 0 ? notBillable / sum : 0.5;
		return [
			["billable", billablePercent],
			["notBillable", notBillablePercent]
		];
	}

	private buildBillableChartNames(billable: number, notBillable: number, sum: number): { [key: string]: string } {
		const billablePercent = sum > 0 ? billable / sum : 0.5;
		const notBillablePercent = sum > 0 ? notBillable / sum : 0.5;
		return {
			billable: this.formatPercent(billablePercent) + " " + messages["statisticsPanel.record.billable"],
			notBillable: this.formatPercent(notBillablePercent) + " " + messages["statisticsPanel.record.notbillable"]
		};
	}

	private createProjectTimeDialog(): ProjectTimeDialog {
		const dialog = new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			users: this.users,
			userSelectionEnabled: globals.isGroupLeader || globals.isTaskLeader,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
		$(dialog).on(ProjectTimeDialog.EVENT_RECORD_EDITED, () => {
			this.reloadTable();
		}).on(ProjectTimeDialog.EVENT_RECORD_CREATED, () => {
			this.filterTable();
		}).on(ProjectTimeDialog.EVENT_RECORD_DELETED, () => {
			this.filterTable();
		});
		return dialog;
	}

	private createProjectTimeDetailsDialog(): ProjectTimeDetailsDialog {
		return new ProjectTimeDetailsDialog({
			title: messages["projectTime.details.header"],
			dialogId: "#projectTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportProjectTime) => this.edit(recording)
		});
	}

	private getProjectTimeDetailsDialog(): ProjectTimeDetailsDialog {
		if (this.projectTimeDetailsDialog == null) {
			this.projectTimeDetailsDialog = this.createProjectTimeDetailsDialog();
		}
		return this.projectTimeDetailsDialog;
	}

	private getProjectTimeDialog(): ProjectTimeDialog {
		if (this.projectTimeDialog == null) {
			this.projectTimeDialog = this.createProjectTimeDialog();
		}
		return this.projectTimeDialog;
	}

	protected getSecurityStatus() {
		return SecurityStatus.isAddProjectTimeAllowed();
	}

	protected createAddRecordDialog(preSelectUser: User) {
		this.getProjectTimeDialog().create(preSelectUser ? {userId: preSelectUser.id} : null, false);
	}

	protected showSelectTaskDialog() {
		this.setTaskDialog.show(this.buildBulkOperationParams(), this.recordingTable.bulkSelector.getSelectedEntriesCount());
	}

	protected showSetNoteDialog() {
		this.setNoteDialog.show(this.buildBulkOperationParams(), this.recordingTable.bulkSelector.getSelectedEntriesCount());
	}

	protected createBulkMenu() {
		const menu = super.createBulkMenu();

		// Project time specific bulk operations
		menu.addDivider();

		menu.addMenuEntry({
			label: messages["report.setTask"],
			action: () => this.showSelectTaskDialog(),
			show: true,
			active: true
		});

		menu.addMenuEntry({
			label: messages["report.setNote"],
			action: () => this.showSetNoteDialog(),
			show: true,
			active: true
		});

		menu.addDivider();

		menu.addMenuEntry({
			label: messages["report.setBillable"],
			action: () => this.bulkOperationSetBillable(),
			show: globals.allowedToChangeBillable,
			active: true
		});

		menu.addMenuEntry({
			label: messages["report.setNotBillable"],
			action: () => this.bulkOperationSetNotBillable(),
			show: globals.allowedToChangeBillable,
			active: true
		});

		// add delete menu item last
		menu.addDivider();
		menu.addMenuEntry({
			label: messages["report.delete"],
			action: () => this.bulkOperationDelete(),
			show: true,
			active: true
		});

		return menu;
	}

	private showInfoDialog(taskId: number, userId: number) {
		if (!this.taskInfoDialog) {
			this.taskInfoDialog = new TaskInfoDialog({
				dialogId: "#taskInfoDialog"
			});
		}
		void api("tasks/loadTaskDetails.json")
			.get({id: taskId})
			.done((e: TaskDetailsResponse) => {
				this.taskInfoDialog.show(e, userId);
			});
	}
}
